import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const [tab, setTab]     = useState(location.state?.tab || 'login');
  const [form, setForm]   = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setError(''); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError(''); setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const tabStyle = (t) => ({
    flex: 1, padding: '12px', fontSize: '0.9rem', cursor: 'pointer',
    background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--gold)' : 'transparent'}`,
    color: tab === t ? 'var(--gold)' : 'var(--t-muted)',
    fontWeight: tab === t ? 700 : 400,
    fontFamily: 'inherit', transition: 'all 0.15s',
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-0)', padding: '20px', paddingTop: 'var(--nav-h)' }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ display: 'inline-block', marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800, color: 'var(--gold)' }}>MilleHills</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--t-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Ecosystem Platform</div>
          </Link>
        </div>

        {/* Card */}
        <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', overflow: 'hidden' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            <button style={tabStyle('login')}    onClick={() => { setTab('login');    setError(''); }}>Sign In</button>
            <button style={tabStyle('register')} onClick={() => { setTab('register'); setError(''); }}>Create Account</button>
          </div>

          <div style={{ padding: '32px' }}>
            {tab === 'login' ? (
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="field">
                  <label>Email Address</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" autoFocus />
                </div>
                <div className="field">
                  <label>Password</label>
                  <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Your password" />
                </div>
                {error && <div style={{ padding: '10px 14px', background: 'rgba(240,96,96,0.1)', border: '1px solid rgba(240,96,96,0.3)', borderRadius: 'var(--r-sm)', color: 'var(--red)', fontSize: '0.875rem' }}>{error}</div>}
                <button type="submit" disabled={loading || !form.email || !form.password} className="btn btn-primary" style={{ justifyContent: 'center', marginTop: 4, opacity: (!form.email || !form.password) ? 0.6 : 1 }}>
                  {loading ? 'Signing in…' : 'Sign In →'}
                </button>
                <div style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--t-muted)' }}>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => { setTab('register'); setError(''); }} style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit' }}>Create one →</button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="field">
                  <label>Full Name</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your full name" autoFocus />
                </div>
                <div className="field">
                  <label>Email Address</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" />
                </div>
                <div className="field">
                  <label>Password</label>
                  <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="At least 8 characters" />
                </div>
                <div className="field">
                  <label>Confirm Password</label>
                  <input type="password" value={form.confirm} onChange={e => set('confirm', e.target.value)} placeholder="Repeat your password" />
                </div>
                {error && <div style={{ padding: '10px 14px', background: 'rgba(240,96,96,0.1)', border: '1px solid rgba(240,96,96,0.3)', borderRadius: 'var(--r-sm)', color: 'var(--red)', fontSize: '0.875rem' }}>{error}</div>}
                <button type="submit" disabled={loading || !form.name || !form.email || !form.password} className="btn btn-primary" style={{ justifyContent: 'center', marginTop: 4, opacity: (!form.name || !form.email || !form.password) ? 0.6 : 1 }}>
                  {loading ? 'Creating account…' : 'Create Account →'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--t-muted)', textAlign: 'center', lineHeight: 1.6 }}>
                  By creating an account you agree to our terms and can enroll in programs and apply for internships.
                </p>
              </form>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/" style={{ fontSize: '0.82rem', color: 'var(--t-muted)' }}>← Back to MilleHills</Link>
        </div>
      </div>
    </div>
  );
}